import * as yup from 'yup'
import { useMemo } from 'react'
import { toast } from 'react-toastify'
import { Add } from '@mui/icons-material'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, CircularProgress, Typography } from '@mui/material'
import { Form } from '../Form/Form'
import { Input } from '../Form/Input'
import { Button } from '../Form/Button'
import { useProposalContext } from '~/contexts'
import { AttachedFiles, RegisteredAnnotation } from './components'
import { ProposalModel, useAddNoteToProposalMutation } from '~/graphql/types'
import { usePermission } from '~/hooks'
import { uploadToS3 } from '~/services/uploadFile'

export type AnnotationForm = {
  annotation: string
  file: File
}

const schema = yup.object().shape({
  annotation: yup.string(),
})

export type AnnotationsProps = {
  proposal: ProposalModel
  customUpdateProposal?: Function
}
export const Annotations: React.FC<AnnotationsProps> = ({ proposal, customUpdateProposal }) => {
  const { notes } = proposal
  const { setProposal } = useProposalContext()
  const [addAnnotationToProposal, { loading }] = useAddNoteToProposalMutation()

  const canAddAnnotations = usePermission(['proposals.proposalAddNote'])

  const renderedAnnotations = useMemo(() => {
    return notes?.map((item, index) => (
      <RegisteredAnnotation
        key={`annotation-${index}`}
        annotation={item.text}
        createdAt={item.createdAt}
        analystName={item.analystName}
        file={item.file}
      />
    ))
  }, [notes])

  const methods = useForm<AnnotationForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      annotation: '',
      file: undefined
    }
  })

  const attachedFile = methods.watch('file')

  const onSubmit = async (formData: AnnotationForm) => {
    const hasAnnotation = formData.annotation?.trim()
    const hasFile = formData.file !== undefined && formData.file !== null
    if (!hasAnnotation && !hasFile) {
      methods.setError('annotation', {
        type: 'manual',
        message: 'Adicione uma observação ou anexe um arquivo'
      })
      return
    }

    try {
      let uploadedFileKey: string | undefined

      if (formData.file) {
        const uploadResult = await uploadToS3([formData.file])
        uploadedFileKey = uploadResult.key ?? undefined
      }

      const { data: updatedProposal } = await addAnnotationToProposal({
        variables: {
          params: {
            proposalId: proposal._id,
            text: formData.annotation || '',
            fileKey: uploadedFileKey
          }
        }
      })

      methods.reset()

      if (customUpdateProposal) {
        updatedProposal && customUpdateProposal(updatedProposal.addNoteToProposal as ProposalModel)
        return
      }

      updatedProposal && setProposal(updatedProposal.addNoteToProposal as ProposalModel)
      toast.success('Observação registrada com sucesso')
    } catch (err) {
      toast.error('Houve um problema ao adicionar observação')
    }
  }

  return (
    <FormProvider {...methods}>
      <Form key='annotations'>
        <Box flexDirection='column' display='flex' gap={3}>
          <Typography variant='h3'>Observações</Typography>

          {renderedAnnotations}

          <Box>
            <Box sx={{
              position: 'relative',
              '& .MuiFormControl-root': {
                marginBottom: 0
              }
            }}>
              <Input
                multiline
                minRows={5}
                name='annotation'
                disabled={loading || !canAddAnnotations}
                placeholder='Escreva aqui suas observações'
                sx={{
                  marginBottom: 0,
                  '& .MuiInputBase-root': {
                    paddingBottom: attachedFile ? '80px' : '48px',
                  }
                }}
              />

              <Box sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                pointerEvents: 'none',
                '& > *': {
                  pointerEvents: 'auto'
                }
              }}>
                <AttachedFiles disabled={!canAddAnnotations} loading={loading} />
              </Box>
            </Box>
          </Box>

          <Button
            color='secondary'
            style={{ alignSelf: 'start' }}
            disabled={loading || !canAddAnnotations}
            onClick={methods.handleSubmit(onSubmit)}
            startIcon={loading ? <CircularProgress color='inherit' size={24} /> : <Add />}
          >
            Registrar observação
          </Button>
        </Box>
      </Form>
    </FormProvider>
  )
}
